import { motion } from 'framer-motion';
import { ArrowUpRight, GraduationCap, Plane, Target } from 'lucide-react';

const Services = () => {
  const services = [
    {
      title: 'Mes Formations',
      description:
        'Vous souhaitez apprendre à votre rythme. Nos programmes sont construits pour vous permettre une totale autonomie d\'apprentissage avec PDF et exercices.',
      link: 'https://gillesdelieuze.com/formations/',
      icon: GraduationCap,
      image: 'https://gillesdelieuze.com/wp-content/uploads/2022/11/3.jpg',
      featured: false,
    },
    {
      title: 'Les Voyages',
      description:
        'Expériences immersives et transformatrices. Nage avec les dauphins sauvages, Madagascar, destinations aux énergies uniques pour un voyage intérieur.',
      link: 'https://gillesdelieuze.com/voyages-gd/',
      icon: Plane,
      image: 'https://gillesdelieuze.com/wp-content/uploads/2022/11/2.jpg',
      featured: true,
    },
    {
      title: 'AXIOM Mentorat',
      description:
        'Pour les leaders qui ont tout accompli mais sentent qu\'ils ne peuvent plus construire leur empire contre eux-mêmes. 9 mois de transformation profonde.',
      link: 'https://gillesdelieuze.com/axiom-cursus-immersion/',
      icon: Target,
      image: 'https://gillesdelieuze.com/wp-content/uploads/2022/11/1.jpg',
      featured: false,
    },
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#FAFAF9]"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-[#D4AF37] uppercase tracking-[0.3em] text-sm font-medium mb-4">
            Accompagnements
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#1C1917] leading-tight">
            Mes <span className="italic text-[#D4AF37]">Services</span>
          </h2>
          <p className="mt-6 text-[#44403C] text-lg max-w-2xl mx-auto">
            Plusieurs chemins pour vous accompagner vers votre vérité profonde et
            votre rayonnement authentique.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <motion.a
              key={service.title}
              href={service.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.15 }}
              className={`group relative block rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-2xl ${
                service.featured
                  ? 'bg-[#1C1917] md:row-span-2 md:-my-8'
                  : 'bg-white border border-[#D4AF37]/10 hover:border-[#D4AF37]/50'
              }`}
              data-testid={`service-card-${index}`}
            >
              <div
                className={`relative h-full flex flex-col ${
                  service.featured ? 'min-h-[450px]' : 'min-h-[320px]'
                }`}
              >
                {/* Background Image */}
                <div className="absolute inset-0 overflow-hidden rounded-2xl">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover object-center opacity-20 group-hover:opacity-30 transition-opacity duration-500"
                  />
                  <div className={`absolute inset-0 ${
                    service.featured 
                      ? 'bg-gradient-to-t from-[#1C1917] via-[#1C1917]/95 to-[#1C1917]/80'
                      : 'bg-gradient-to-t from-white via-white/98 to-white/90'
                  }`} />
                </div>
                
                <div className={`relative p-8 lg:p-10 flex flex-col h-full`}>
                  {/* Icon */}
                  <div
                    className={`mb-6 w-14 h-14 rounded-full flex items-center justify-center ${
                      service.featured
                        ? 'bg-[#D4AF37]/20'
                        : 'bg-[#D4AF37]/10 group-hover:bg-[#D4AF37]/20'
                    } transition-colors`}
                  >
                    <service.icon
                      className="text-[#D4AF37]"
                      size={24}
                      strokeWidth={1.5}
                    />
                  </div>

                  {/* Content */}
                  <h3
                    className={`font-display text-2xl lg:text-3xl mb-4 ${
                      service.featured ? 'text-[#FAFAF9]' : 'text-[#1C1917]'
                    }`}
                  >
                    {service.title}
                  </h3>
                  <p
                    className={`leading-relaxed flex-grow ${
                      service.featured ? 'text-[#A8A29E]' : 'text-[#44403C]'
                    }`}
                  >
                    {service.description}
                  </p>

                  {/* CTA */}
                  <div
                    className={`mt-6 flex items-center gap-2 font-medium uppercase tracking-wider text-sm ${
                      service.featured
                        ? 'text-[#D4AF37]'
                        : 'text-[#D4AF37] group-hover:text-[#AA8C2C]'
                    } transition-colors`}
                  >
                    Découvrir
                    <ArrowUpRight
                      size={18}
                      className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                    />
                  </div>

                  {/* Featured Badge */}
                  {service.featured && (
                    <div className="absolute top-6 right-6">
                      <span className="bg-[#D4AF37] text-[#1C1917] text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full">
                        Phare
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Hover Gradient */}
              <div
                className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none ${
                  service.featured
                    ? 'bg-gradient-to-t from-[#D4AF37]/10 to-transparent'
                    : 'bg-gradient-to-t from-[#D4AF37]/5 to-transparent'
                }`}
              />
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
